import { API_AUTH_ERRORS } from '@infotech-soe/merps/types/errors';
import {
  AuthData,
  AuthMethod,
  ProjectInfo,
} from '@infotech-soe/merps/types/session';
import { SignatureType } from '@infotech-soe/metaschema';
import {
  DSAgents,
  KeyTypes,
  SignAlgo,
} from '@infotech-soe/mia-ds-service/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AvailableApplication, FeaturesList } from '../../services/auth/types';
import { CommandName } from '../../services/commands/CommandViewModel';
import UniqueId from '../../services/uid/UniqueId';

import { failureAction, pendingAction, successAction } from './actionHandler';

const SUPPORTED_AUTH_METHODS: AuthMethod[] = [
  AuthMethod.DIGITAL_SIGNATURE,
  AuthMethod.LOGIN,
  AuthMethod.WINDOWS_AD,
  AuthMethod.KERBEROS,
];

export type SignFormState = {
  open: boolean;
  signatureType: SignatureType;
  datasetId: UniqueId;
  command: CommandName;
  signData: string[];
};

export const enum AuthResult {
  YES,
  NO,
  IN_PROGRESS,
}

export interface SessionState {
  application: string | null;
  authenticated: AuthResult;
  authMethods: Partial<Record<AuthMethod, boolean>>;
  availableApps: AvailableApplication[];
  userApps: AvailableApplication[];
  availableDSAgents: DSAgents[];
  availableDSKeyTypes: KeyTypes[];
  allowedDSMediaTypes: string | null;
  forbiddenDSMediaTypes: string | null;
  error: boolean;
  login: string;
  requiredMethod?: AuthMethod;
  serverTimeZone: string[];
  userName: string | undefined;
  projectInfo: ProjectInfo;
  signFormProps?: SignFormState;
  signAlgo?: SignAlgo;
}

const initialState: SessionState = {
  application: null,
  authenticated: AuthResult.NO,
  authMethods: {},
  availableApps: [],
  userApps: [],
  availableDSAgents: [],
  availableDSKeyTypes: [],
  allowedDSMediaTypes: null,
  forbiddenDSMediaTypes: null,
  error: false,
  login: '',
  serverTimeZone: ['Europe/Kiev'],
  userName: undefined,
  projectInfo: { merpsVersion: '' },
  signFormProps: undefined,
  signAlgo: undefined,
};

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    signIn: pendingAction<
      SessionState,
      AuthData & { application: string | null }
    >(),
    signInSuccess: successAction<
      SessionState,
      {
        type: AuthMethod;
        login: string;
        application: string | null;
        userName?: string;
        projectInfo: ProjectInfo;
      }
    >((state, { payload: { login, application, userName, projectInfo } }) => {
      state.application = application;
      state.authenticated = AuthResult.YES;
      state.error = false;
      state.login = login;
      state.userName = userName;
      state.projectInfo = projectInfo;
    }),
    signInFailure: failureAction<
      SessionState,
      Error & { code?: API_AUTH_ERRORS }
    >((state, { payload: { code } }) => {
      state.authenticated = AuthResult.NO;
      state.error = true;

      if (code === API_AUTH_ERRORS.REQUIRED_SMART_CARD) {
        state.requiredMethod = AuthMethod.DIGITAL_SIGNATURE;
      }
    }),
    reconnection: pendingAction<
      SessionState,
      AuthData & { application: string }
    >(),
    reconnectionSuccess: successAction<
      SessionState,
      { authenticated: AuthResult }
    >((state, { payload: { authenticated } }) => {
      state.authenticated = authenticated;
      state.error = false;
    }),
    reconnectionFailure: failureAction(),
    signOut: pendingAction(),
    signOutSuccess: successAction<SessionState>(state => {
      state.application = null;
      state.authenticated = AuthResult.NO;
      state.error = false;
      state.login = '';
      state.userName = undefined;
    }),
    signOutFailure: failureAction(),
    bindStateToExistingSession(
      state,
      {
        payload: { login, application },
      }: PayloadAction<{ login: string; application: string | null }>
    ) {
      state.authenticated = AuthResult.YES;
      state.error = false;
      state.login = login;
      state.application = application;
    },
    loadFeatures: pendingAction(),
    loadFeaturesSuccess: successAction<SessionState, FeaturesList>(
      (
        state,
        {
          payload: {
            apps,
            userApps,
            excludedEDSAgents,
            dsKeyType,
            allowedDSMediaTypes,
            forbiddenDSMediaTypes,
            authMethods,
            timezone,
            signAlgo,
          },
        }
      ) => {
        state.availableApps = apps;
        state.userApps = userApps;
        state.availableDSAgents = [DSAgents.CIPHER, DSAgents.IIT].filter(
          agent => !excludedEDSAgents.includes(agent)
        );
        state.availableDSKeyTypes = dsKeyType
          ? [dsKeyType]
          : [KeyTypes.FILE, KeyTypes.DEVICE];
        state.allowedDSMediaTypes = allowedDSMediaTypes ?? null;
        state.forbiddenDSMediaTypes = forbiddenDSMediaTypes ?? null;
        SUPPORTED_AUTH_METHODS.forEach(type => {
          state.authMethods[type] = authMethods[type];
        });
        state.serverTimeZone = timezone;
        state.signAlgo = signAlgo;
      }
    ),
    loadFeaturesFailure: failureAction(),
    closeSignForm: state => (state.signFormProps = undefined),
    openSignForm: (
      state,
      {
        payload,
      }: PayloadAction<{
        datasetId: UniqueId;
        command: CommandName;
        signatureType: SignatureType;
      }>
    ) => {
      state.signFormProps = { ...payload, open: true, signData: [] };
    },
  },
});

export const sessionReducer = sessionSlice.reducer;

export default sessionSlice.actions;
